import { useState, useEffect, useCallback } from 'react';
import { userAPI } from '../services/api';

/**
 * Custom hook for checking user permissions
 *
 * Returns:
 * - user: user object with subscription info
 * - loading: boolean
 * - error: error object if any
 * - hasFeatureAccess: function to check feature access
 * - hasModelAccess: function to check model access
 * - checkAccess: function to check access and show upgrade prompt if needed
 */
const usePermission = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load user profile on mount
  useEffect(() => {
    const loadUser = async () => {
      try {
        setLoading(true);
        const response = await userAPI.getProfile();
        if (response.data.success) {
          setUser(response.data.data);
        }
      } catch (err) {
        console.error('Failed to load user profile:', err);
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    loadUser();
  }, []);

  /**
   * Check if user has access to a feature
   * @param {string} featureId - Feature identifier (e.g., 'blog-posts', 'poster')
   * @returns {boolean}
   */
  const hasFeatureAccess = useCallback((featureId) => {
    if (!user || !user.subscription) return false;
    const availableFeatures = user.subscription.available_features || [];
    return availableFeatures.includes(featureId);
  }, [user]);

  /**
   * Check if user has access to a model
   * @param {string} modelId - Model identifier (e.g., 'deepseek-chat', 'qwen3-vl-plus')
   * @returns {boolean}
   */
  const hasModelAccess = useCallback((modelId) => {
    if (!user || !user.subscription) return false;
    const availableModels = user.subscription.available_models || [];
    return availableModels.includes(modelId);
  }, [user]);

  /**
   * Get available models for user's plan
   * @returns {Array<string>}
   */
  const getAvailableModels = useCallback(() => {
    if (!user || !user.subscription) return [];
    return user.subscription.available_models || [];
  }, [user]);

  /**
   * Get available features for user's plan
   * @returns {Array<string>}
   */
  const getAvailableFeatures = useCallback(() => {
    if (!user || !user.subscription) return [];
    return user.subscription.available_features || [];
  }, [user]);

  /**
   * Get user's plan information
   * @returns {Object}
   */
  const getPlanInfo = useCallback(() => {
    if (!user || !user.subscription) {
      return {
        name: 'Free',
        slug: 'free',
        tier: 'free'
      };
    }
    return {
      name: user.subscription.plan_name,
      slug: user.subscription.plan_slug,
      tier: user.subscription.plan_slug
    };
  }, [user]);

  /**
   * Get required plan for a feature
   * @param {string} featureId
   * @returns {string}
   */
  const getRequiredPlanForFeature = (featureId) => {
    // Free plan features
    const freeFeatures = ['ai-avatar', 'ad-copy', 'email-generation', 'product-descriptions'];
    if (freeFeatures.includes(featureId)) return 'Free';

    // Basic plan features (also available to Professional)
    const basicFeatures = ['blog-posts', 'poster', 'ai-image-generation'];
    if (basicFeatures.includes(featureId)) return 'Basic';

    // Professional plan features
    return 'Professional';
  };

  /**
   * Get required plan for a model
   * @param {string} modelId
   * @returns {string}
   */
  const getRequiredPlanForModel = (modelId) => {
    // Free/Basic models
    const basicModels = ['deepseek-chat', 'deepseek-reasoner'];
    if (basicModels.includes(modelId)) return 'Free';

    // Professional models (actually available from Basic tier)
    return 'Basic';
  };

  return {
    user,
    loading,
    error,
    hasFeatureAccess,
    hasModelAccess,
    getAvailableModels,
    getAvailableFeatures,
    getPlanInfo,
    getRequiredPlanForFeature,
    getRequiredPlanForModel
  };
};

export default usePermission;
